import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ROUTES, checklistInspect } from '../../../constants/routes';
import { AuthContext } from '../../../context/AuthContext';
import ChecklistRatingQuestionRow from '../../../components/inspection/ChecklistRatingQuestionRow';
import {
  RATING_COLUMN_LABEL,
  RATING_COLUMN_STYLES,
  RATING_KEYS,
} from '../../../components/inspection/checklistRatingConstants';

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function nowDatetimeLocal() {
  return toDatetimeLocal(new Date().toISOString());
}

function ChecklistInspectionForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const viewOnly =
    searchParams.get('view') === '1' || searchParams.get('view') === 'true';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [inspectorName, setInspectorName] = useState('');
  const [inspectedAtLocal, setInspectedAtLocal] = useState('');
  const [ratings, setRatings] = useState({});
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const readOnly = useMemo(() => {
    if (!data?.inspection) return true;
    return viewOnly || data.inspection.status === 'submitted';
  }, [data, viewOnly]);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.get(`/api/checklist/inspections/${id}`, { headers: authHeader() });
      setData(res.data);
      const ins = res.data.inspection;
      const isDraft = ins.status === 'draft';
      const isViewOnly =
        searchParams.get('view') === '1' || searchParams.get('view') === 'true';

      let name = ins.inspectorName || '';
      let inspectedLocal = toDatetimeLocal(ins.inspectedAt);

      if (!isViewOnly && isDraft) {
        if (!String(name).trim() && user?.name) {
          name = user.name;
        }
        if (!inspectedLocal) {
          inspectedLocal = nowDatetimeLocal();
        }
      }

      setInspectorName(name);
      setInspectedAtLocal(inspectedLocal);
      const r = {};
      const n = {};
      for (const z of res.data.zones || []) {
        for (const g of z.groups || []) {
          for (const q of g.questions || []) {
            if (q.answer) {
              r[q.id] = q.answer.rating || '';
              n[q.id] = q.answer.note || '';
            } else {
              r[q.id] = '';
              n[q.id] = '';
            }
          }
        }
      }
      setRatings(r);
      setNotes(n);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id, user?.name, searchParams]);

  useEffect(() => {
    load();
  }, [load]);

  const flatQuestions = useMemo(() => {
    const list = [];
    if (!data?.zones) return list;
    for (const z of data.zones) {
      for (const g of z.groups || []) {
        for (const q of g.questions || []) {
          list.push({ zone: z.name, group: g.name, question: q });
        }
      }
    }
    return list;
  }, [data]);

  const buildAnswersPayload = () =>
    flatQuestions.map(({ question: q }) => ({
      questionId: q.id,
      rating: ratings[q.id] || null,
      note: notes[q.id] ?? '',
    }));

  const persistDraft = async () => {
    if (readOnly) return true;
    try {
      await axios.patch(
        `/api/checklist/inspections/${id}`,
        {
          inspectorName,
          inspectedAt: fromDatetimeLocal(inspectedAtLocal),
          answers: buildAnswersPayload(),
        },
        { headers: authHeader() }
      );
      return true;
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
      return false;
    }
  };

  const saveDraft = async () => {
    if (readOnly) return;
    setSaving(true);
    setError('');
    try {
      const ok = await persistDraft();
      if (ok) await load();
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (readOnly) return;
    setSaving(true);
    setError('');
    try {
      await axios.patch(
        `/api/checklist/inspections/${id}`,
        {
          inspectorName,
          inspectedAt: fromDatetimeLocal(inspectedAtLocal),
          answers: buildAnswersPayload(),
        },
        { headers: authHeader() }
      );
      await axios.post(`/api/checklist/inspections/${id}/submit`, {}, { headers: authHeader() });
      navigate(ROUTES.RAISE_THE_SAIL_REPORTS);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Submit failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Discard this inspection? Unsaved changes may be lost.')) return;
    if (data?.inspection?.status === 'draft') {
      if (window.confirm('Delete this draft from the server?')) {
        try {
          await axios.delete(`/api/checklist/inspections/${id}`, { headers: authHeader() });
        } catch (e) {
          /* still navigate */
        }
      }
    }
    navigate(ROUTES.RAISE_THE_SAIL);
  };

  const setRating = (qid, value) => {
    if (readOnly) return;
    setRatings((prev) => ({ ...prev, [qid]: value }));
  };

  const setNote = (qid, value) => {
    if (readOnly) return;
    setNotes((prev) => ({ ...prev, [qid]: value }));
  };

  const uploadPhoto = async (answerId, file) => {
    if (!file || readOnly) return;
    const fd = new FormData();
    fd.append('image', file);
    try {
      await axios.post(`/api/checklist/inspections/${id}/answers/${answerId}/images`, fd, {
        headers: { ...authHeader() },
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Upload failed');
    }
  };

  const deletePhoto = async (imageId, answerId) => {
    if (readOnly) return;
    try {
      await axios.delete(`/api/checklist/inspections/${id}/images/${imageId}`, {
        headers: authHeader(),
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Delete failed');
    }
  };

  const openServiceRequest = async (q) => {
    setSaving(true);
    setError('');
    try {
      const ok = await persistDraft();
      if (!ok) return;
      const text = [q.questionText, notes[q.id]].filter(Boolean).join(' — ');
      const params = new URLSearchParams();
      params.set('propertyId', String(data.inspection.propertyId));
      params.set('problem', text.slice(0, 2000));
      params.set('returnTo', checklistInspect(id));
      navigate(`${ROUTES.SERVICE_REQUESTS_NEW}?${params.toString()}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="app-loading text-muted" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        <span>Loading inspection…</span>
      </div>
    );
  }
  if (error && !data) return <div className="alert alert-danger">{error}</div>;
  if (!data) return null;

  return (
    <div className="app-shell-page">
      {error && <div className="alert alert-warning py-2 mb-2">{error}</div>}
      {readOnly && (
        <div className="alert alert-info py-2 mb-3">View only (submitted or read-only mode).</div>
      )}

      <div className="card app-card mb-3 bg-white">
        <div className="card-body">
          {data.inspection.propertyName && (
            <div className="mb-3 pb-3 border-bottom">
              <div className="text-muted small mb-1">Property</div>
              <div className="h5 mb-0">{data.inspection.propertyName}</div>
            </div>
          )}
          <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Inspector name</label>
            <input
              type="text"
              className="form-control"
              value={inspectorName}
              onChange={(e) => setInspectorName(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Inspection date & time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={inspectedAtLocal}
              onChange={(e) => setInspectedAtLocal(e.target.value)}
              disabled={readOnly}
            />
          </div>
          </div>
        </div>
      </div>

      {data.zones.map((z) => (
        <div key={z.id} className="card app-card mb-4 bg-white">
          <div className="card-body">
            <h2 className="h5 text-primary border-bottom pb-2 mb-3">{z.name}</h2>
            {z.groups.map((g) => (
              <div key={g.id} className="card app-card mb-3 bg-white">
                <div className="card-body py-3">
                  <h3 className="h6 text-secondary mb-2">{g.name}</h3>
                  <div className="app-table-wrap inspection-question-sheet">
                    <div className="inspection-question-sheet-header inspection-question-sheet-header--rating d-none d-md-grid">
                      <div>Question</div>
                      {RATING_KEYS.map((c) => (
                        <div
                          key={c}
                          className="text-center small"
                          style={{ backgroundColor: RATING_COLUMN_STYLES[c].header }}
                        >
                          {RATING_COLUMN_LABEL[c]}
                        </div>
                      ))}
                      <div>Note</div>
                      <div>Photo</div>
                      <div>SR</div>
                    </div>
                    {g.questions.map((q) => (
                      <ChecklistRatingQuestionRow
                        key={q.id}
                        question={q}
                        answer={q.answer}
                        rating={ratings[q.id]}
                        note={notes[q.id]}
                        readOnly={readOnly}
                        onRatingChange={setRating}
                        onNoteChange={setNote}
                        onUploadPhoto={uploadPhoto}
                        onDeletePhoto={deletePhoto}
                        onCreateServiceRequest={openServiceRequest}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {!readOnly && (
        <div className="d-flex flex-wrap gap-2 mb-4">
          <button type="button" className="btn btn-secondary" disabled={saving} onClick={saveDraft}>
            Save draft
          </button>
          <button type="button" className="btn btn-success" disabled={saving} onClick={submit}>
            Submit
          </button>
          <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={cancel}>
            Cancel
          </button>
        </div>
      )}

      {readOnly && (
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate(ROUTES.RAISE_THE_SAIL_REPORTS)}
        >
          Back to reports
        </button>
      )}
    </div>
  );
}

export default ChecklistInspectionForm;
